import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { recruitingAPI } from "../services/recruitingApi";
import api, { axiosInstance } from "../services/api";
import { getCurrentUser } from "../utils/auth";
import { CandidateStatusBadge } from "./CandidateStatusControl";
import { User } from "../types/shared.types";
import {
  FileUploadWidget,
  CompanyInfoSection,
  ProposalField,
} from "./TechCheck";
import { useProposals } from "../hooks/useProposals";
import "./TechCheckForm.css";

interface CandidateInfo {
  id: string | number;
  role_type?: string;
  main_skill?: string;
  seniority?: string;
  personality_type?: string;
  english_level?: string;
  expected_rate?: string | number;
  notice_period?: string;
  door_opener?: boolean;
  location?: string;
  source?: string;
  linkedin_url?: string;
  blind_cv_url?: string;
  resume_url?: string;
  status?: string;
}

interface JobInfo {
  id: number;
  title: string;
  department?: string;
  status?: string;
  customer_id?: number;
}

interface TechCheckFormProps {
  candidateId: string | number;
  candidate?: CandidateInfo;
  jobs?: JobInfo[];
}

interface FormData {
  it_experience: string;
  reasons_for_change: string;
  what_looking_for: string;
  overall_impression: string;
  other_info: string;
  performed_by: number | null;
  date: string;
  blind_cv_review: string;
  survey_complete: boolean;
  survey_link: string;
  decision: string;
  [key: string]: string | number | boolean | null;
}

interface Note {
  id: number;
  content: string;
  author_name?: string;
  created_at: string;
}

interface Company {
  company_name?: string;
  name?: string;
  industry?: string;
  company_size?: string;
  employee_count?: number;
  founded_year?: number;
  description?: string;
  website?: string;
}

interface TechCheckData {
  it_experience?: string;
  reasons_for_change?: string;
  what_looking_for?: string;
  overall_impression?: string;
  other_info?: string;
  performed_by?: number | null;
  date?: string;
  blind_cv_review?: string;
  survey_complete?: boolean;
  survey_link?: string;
  decision?: string;
}

interface ProposalData {
  value: string;
  isProposal: boolean;
}

interface ProposalsResponse {
  success?: boolean;
  data?: {
    proposals?: Record<string, ProposalData>;
  };
}

const TechCheckForm: React.FC<TechCheckFormProps> = ({
  candidateId,
  candidate,
  jobs = [],
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const user = (getCurrentUser() || {}) as User;
  const isTechChecker = user.role === "tech_checker";
  const [formData, setFormData] = useState<FormData>({
    it_experience: "",
    reasons_for_change: "",
    what_looking_for: "",
    overall_impression: "",
    other_info: "",
    performed_by: null,
    date: "",
    blind_cv_review: "",
    survey_complete: false,
    survey_link: "",
    decision: "",
  });
  const {
    proposals,
    isProposal,
    setProposalsFromResponse,
    acceptProposal,
    rejectProposal,
    clearProposalStatus,
  } = useProposals();
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [uploadingCV, setUploadingCV] = useState(false);
  const [uploadingBlindCV, setUploadingBlindCV] = useState(false);

  // Helper function to convert door_opener boolean to select value
  const getDoorOpenerValue = (doorOpener?: boolean): string => {
    if (doorOpener === true) return "yes";
    if (doorOpener === false) return "no";
    return "";
  };

  // Helper function to parse door_opener select value to boolean
  const parseDoorOpenerValue = (value: string): boolean | null => {
    if (value === "yes") return true;
    if (value === "no") return false;
    return null;
  };

  // Fetch tech check data
  const { isLoading } = useQuery<{ success?: boolean; data?: TechCheckData }>(
    ["tech-check", candidateId],
    () =>
      recruitingAPI.getTechCheck(candidateId) as Promise<{
        success?: boolean;
        data?: TechCheckData;
      }>,
    {
      enabled: !!candidateId,
      onSuccess: (data) => {
        if (data.success && data.data) {
          const tc = data.data;
          const today = new Date().toISOString().split("T")[0];
          setFormData({
            it_experience: tc.it_experience || "",
            reasons_for_change: tc.reasons_for_change || "",
            what_looking_for: tc.what_looking_for || "",
            overall_impression: tc.overall_impression || "",
            other_info: tc.other_info || "",
            performed_by: tc.performed_by || null,
            date: tc.date ? tc.date.split("T")[0] : today,
            blind_cv_review: tc.blind_cv_review || "",
            survey_complete: tc.survey_complete || false,
            survey_link: tc.survey_link || "",
            decision: tc.decision || "",
          });
        } else {
          // If no tech check data exists, prefill date to today
          const today = new Date().toISOString().split("T")[0];
          setFormData((prev) => ({
            ...prev,
            date: prev.date || today,
          }));
        }
      },
    },
  );

  // Fetch users for performed_by selector
  const { data: usersData } = useQuery<User[]>(
    "users",
    () => api.admin.getAllUsers(),
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  // Set default job ID from first active application
  useEffect(() => {
    if (jobs.length > 0 && !selectedJobId) {
      const activeJob = jobs.find((j) => j.status === "active") || jobs[0];
      if (activeJob) {
        setSelectedJobId(activeJob.id);
      }
    }
  }, [jobs, selectedJobId]);

  // Get selected job details
  const selectedJob = jobs.find((j) => j.id === selectedJobId);

  // Fetch company information when job has customer_id
  const { data: companyData } = useQuery<{ data?: Company }>(
    ["company", selectedJob?.customer_id],
    () =>
      recruitingAPI.getCustomer(selectedJob?.customer_id) as Promise<{
        data?: Company;
      }>,
    {
      enabled: !!selectedJob?.customer_id,
    },
  );

  // Fetch company notes
  const { data: companyNotesData } = useQuery<{ data?: Note[] }>(
    ["company-notes", selectedJob?.customer_id],
    () =>
      recruitingAPI.getNotes("customer", selectedJob?.customer_id) as Promise<{
        data?: Note[];
      }>,
    {
      enabled: !!selectedJob?.customer_id,
    },
  );

  const company = companyData?.data;
  const companyNotes = companyNotesData?.data || [];

  // Update mutation
  const updateMutation = useMutation(
    (data: Partial<FormData>) =>
      recruitingAPI.updateTechCheck(candidateId, data),
    {
      onSuccess: () => {
        setSaveStatus(t("techCheck.savedSuccessfully"));
        queryClient.invalidateQueries(["tech-check", candidateId]);
        setTimeout(() => setSaveStatus(""), 3000);
      },
      onError: () => {
        setSaveStatus(t("techCheck.errorSaving"));
        setTimeout(() => setSaveStatus(""), 3000);
      },
    },
  );

  // Generate proposals mutation
  const generateMutation = useMutation<ProposalsResponse, Error>(
    () =>
      recruitingAPI.generateTechCheckProposals(
        candidateId,
        selectedJobId,
      ) as Promise<ProposalsResponse>,
    {
      onSuccess: (response) => {
        if (response.success && response.data?.proposals) {
          setProposalsFromResponse(response.data.proposals);

          // Auto-fill form fields with proposals
          setFormData((prev) => {
            const updated = { ...prev };
            Object.keys(response.data!.proposals!).forEach((key) => {
              const proposalValue = response.data!.proposals![key].value;
              if (proposalValue) {
                updated[key] = proposalValue;
              }
            });
            return updated;
          });

          setIsGenerating(false);
        }
      },
      onError: () => {
        setIsGenerating(false);
      },
    },
  );

  const handleInputChange = (
    field: string,
    value: string | boolean | number | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear proposal status when user edits
    clearProposalStatus(field);
  };

  const handleGenerateProposals = async () => {
    if (!selectedJobId) {
      alert(t("techCheck.selectJobFirst"));
      return;
    }
    setIsGenerating(true);
    generateMutation.mutate();
  };

  const handleAcceptProposal = (field: string) => {
    acceptProposal(field, (value) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    });
  };

  const handleRejectProposal = (field: string) => {
    rejectProposal(field);
  };

  const handleSave = () => {
    const dataToSave = {
      ...formData,
      performed_by: formData.performed_by || null,
      date: formData.date || null,
      survey_complete: formData.survey_complete || false,
    };
    updateMutation.mutate(dataToSave);
  };

  const handleFileUpload = async (file: File, type: "cv" | "blind_cv") => {
    try {
      if (type === "cv") {
        setUploadingCV(true);
      } else {
        setUploadingBlindCV(true);
      }

      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("uploadType", `candidate_${type}`);
      formDataUpload.append("candidateId", String(candidateId));

      const response = (await axiosInstance.post(
        "/files/upload",
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      )) as {
        data?: {
          success?: boolean;
          data?: { file_path?: string; file_url?: string };
        };
      };

      if (response.data?.success) {
        const fileUrl =
          response.data.data?.file_path || response.data.data?.file_url;

        // Update candidate with file URL
        const updateData =
          type === "cv" ? { resume_url: fileUrl } : { blind_cv_url: fileUrl };

        await api.recruiting.updateCandidate(candidateId, updateData);
        queryClient.invalidateQueries(["candidate", candidateId]);

        setSaveStatus(
          type === "cv"
            ? t("techCheck.cvUploaded")
            : t("techCheck.blindCvUploaded"),
        );
        setTimeout(() => setSaveStatus(""), 3000);
      }
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      setSaveStatus(
        type === "cv"
          ? t("techCheck.errorUploadingCv")
          : t("techCheck.errorUploadingBlindCv"),
      );
      setTimeout(() => setSaveStatus(""), 3000);
    } finally {
      if (type === "cv") {
        setUploadingCV(false);
      } else {
        setUploadingBlindCV(false);
      }
    }
  };

  const users = usersData || [];

  if (isLoading) {
    return (
      <div className="tech-check-loading">{t("techCheck.loadingData")}</div>
    );
  }

  // Helper to render a proposal field
  const renderProposalField = (
    field: string,
    label: string,
    isTextarea = true,
  ) => (
    <ProposalField
      field={field}
      label={label}
      value={String(formData[field] || "")}
      proposal={proposals[field]}
      isProposal={isProposal[field] || false}
      onChange={(value) => handleInputChange(field, value)}
      onAccept={() => handleAcceptProposal(field)}
      onReject={() => handleRejectProposal(field)}
      isTextarea={isTextarea}
    />
  );

  return (
    <div className="tech-check-form">
      <div className="tech-check-header">
        <h2>{t("techCheck.title")}</h2>
        <div className="tech-check-actions">
          {!isTechChecker && jobs.length > 0 && (
            <select
              value={selectedJobId || ""}
              onChange={(e) =>
                setSelectedJobId(Number.parseInt(e.target.value, 10))
              }
              className="job-selector"
            >
              <option value="">{t("techCheck.selectJobForAi")}</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title} {job.department ? `(${job.department})` : ""}
                </option>
              ))}
            </select>
          )}
          {!isTechChecker && (
            <button
              onClick={handleGenerateProposals}
              disabled={!selectedJobId || isGenerating}
              className="btn-primary"
            >
              {isGenerating
                ? t("techCheck.generating")
                : t("techCheck.generateAiProposals")}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={updateMutation.isLoading}
            className="btn-success"
          >
            {updateMutation.isLoading
              ? t("techCheck.saving")
              : t("common:actions.save")}
          </button>
          {saveStatus && (
            <span
              className={`save-status ${saveStatus.includes("Error") ? "error" : "success"}`}
            >
              {saveStatus}
            </span>
          )}
        </div>
      </div>

      <div className="tech-check-content">
        {/* Left Column */}
        <div className="tech-check-left">
          <h3>{t("techCheck.candidateDescription")}</h3>
          {renderProposalField("it_experience", t("techCheck.itExperience"))}
          {renderProposalField(
            "reasons_for_change",
            t("techCheck.reasonsForChange"),
          )}
          {renderProposalField(
            "what_looking_for",
            t("techCheck.whatLookingFor"),
          )}
          {renderProposalField(
            "overall_impression",
            t("techCheck.overallImpression"),
          )}
          {renderProposalField("other_info", t("techCheck.otherInfo"))}
        </div>

        {/* Middle Column */}
        <div className="tech-check-middle">
          <div className="tech-check-section">
            <h3>{t("techCheck.basicInfo")}</h3>
            <div className="info-field">
              <span className="info-label">{t("techCheck.roleType")}</span>
              <div className="info-value">{candidate?.role_type || "N/A"}</div>
            </div>
            <div className="info-field">
              <span className="info-label">{t("techCheck.mainSkill")}</span>
              <div className="info-value">{candidate?.main_skill || "N/A"}</div>
            </div>
            <div className="info-field">
              <span className="info-label">
                {t("techCheck.seniorityLevel")}
              </span>
              <div className="info-value">{candidate?.seniority || "N/A"}</div>
            </div>
            <div className="tech-check-field">
              <label htmlFor="personality-type">
                {t("techCheck.personalityType")}
              </label>
              <select
                id="personality-type"
                value={candidate?.personality_type || ""}
                onChange={(e) => {
                  // Update candidate personality type
                  if (candidate) {
                    api.recruiting
                      .updateCandidate(candidate.id, {
                        personality_type: e.target.value,
                      })
                      .then(() => {
                        queryClient.invalidateQueries([
                          "candidate",
                          candidate.id,
                        ]);
                      });
                  }
                }}
              >
                <option value="">{t("techCheck.select")}</option>
                <option value="Introvert">{t("techCheck.introvert")}</option>
                <option value="Extrovert">{t("techCheck.extrovert")}</option>
              </select>
            </div>
            <div className="info-field">
              <span className="info-label">{t("techCheck.englishSkills")}</span>
              <div className="info-value">
                {candidate?.english_level || "N/A"}
              </div>
            </div>
            <div className="info-field">
              <span className="info-label">{t("techCheck.expectedRate")}</span>
              <div className="info-value">
                {candidate?.expected_rate
                  ? `$${candidate.expected_rate}`
                  : "N/A"}
              </div>
            </div>
            <div className="info-field">
              <span className="info-label">{t("techCheck.noticePeriod")}</span>
              <div className="info-value">
                {candidate?.notice_period || "N/A"}
              </div>
            </div>
            <div className="tech-check-field">
              <label htmlFor="tech-check-door-opener">
                {t("techCheck.doorOpener")}
              </label>
              <select
                id="tech-check-door-opener"
                value={getDoorOpenerValue(candidate?.door_opener)}
                onChange={(e) => {
                  // Update candidate door opener
                  const value = parseDoorOpenerValue(e.target.value);
                  if (candidate) {
                    api.recruiting
                      .updateCandidate(candidate.id, { door_opener: value })
                      .then(() => {
                        queryClient.invalidateQueries([
                          "candidate",
                          candidate.id,
                        ]);
                      });
                  }
                }}
              >
                <option value="">{t("techCheck.select")}</option>
                <option value="yes">{t("common:misc.yes")}</option>
                <option value="no">{t("common:misc.no")}</option>
              </select>
            </div>
          </div>

          <div className="tech-check-section">
            <h3>{t("techCheck.other")}</h3>
            <div className="info-field">
              <span className="info-label">
                {t("techCheck.locationResidence")}
              </span>
              <div className="info-value">{candidate?.location || "N/A"}</div>
            </div>
            <div className="info-field">
              <span className="info-label">{t("techCheck.searchChannel")}</span>
              <div className="info-value">{candidate?.source || "N/A"}</div>
            </div>
          </div>

          {/* Company Information Section */}
          {company && (
            <CompanyInfoSection company={company} notes={companyNotes} />
          )}
        </div>

        {/* Right Column */}
        <div className="tech-check-right">
          <div className="tech-check-section">
            <h3>{t("techCheck.statusAndDecision")}</h3>
            {/* Current Status Display */}
            <div className="tech-check-field">
              <label>{t("techCheck.currentStatus")}</label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "4px",
                }}
              >
                <CandidateStatusBadge
                  status={candidate?.status || "preparation"}
                  size="medium"
                />
              </div>
            </div>
            <div className="tech-check-field">
              <label htmlFor="tech-check-decision">
                {t("techCheck.techCheckDecision")}
              </label>
              <select
                id="tech-check-decision"
                value={formData.decision}
                onChange={(e) => handleInputChange("decision", e.target.value)}
              >
                <option value="">{t("techCheck.selectDecision")}</option>
                <option value="approved">{t("techCheck.approved")}</option>
                <option value="rejected">{t("techCheck.rejected")}</option>
                <option value="pending">
                  {t("techCheck.pendingDecision")}
                </option>
                <option value="undecided">{t("techCheck.undecided")}</option>
              </select>
              <small
                style={{
                  color: "#6b7280",
                  fontSize: "11px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {t("techCheck.decisionHint")}
              </small>
            </div>
            <div className="tech-check-field">
              <label htmlFor="tech-check-performed-by">
                {t("techCheck.performedBy")}
              </label>
              <select
                id="tech-check-performed-by"
                value={formData.performed_by || ""}
                onChange={(e) =>
                  handleInputChange(
                    "performed_by",
                    e.target.value ? Number.parseInt(e.target.value, 10) : null,
                  )
                }
              >
                <option value="">{t("techCheck.noOneSelected")}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username}
                  </option>
                ))}
              </select>
            </div>
            <div className="tech-check-field">
              <label htmlFor="tech-check-date">
                {t("techCheck.techCheckDate")}
              </label>
              <input
                id="tech-check-date"
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange("date", e.target.value)}
              />
            </div>
            <div className="info-field">
              <span className="info-label">
                {t("techCheck.linkedinProfile")}
              </span>
              <div className="info-value">
                {candidate?.linkedin_url ? (
                  <a
                    href={candidate.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {candidate.linkedin_url}
                  </a>
                ) : (
                  "N/A"
                )}
              </div>
            </div>
            <div className="info-field">
              <span className="info-label">{t("techCheck.lnInvitation")}</span>
              <div className="info-value">{t("common:misc.no")}</div>
            </div>
            <FileUploadWidget
              label={t("common:fileUpload.blindCvLabel")}
              fileUrl={candidate?.blind_cv_url}
              onUpload={(file) => handleFileUpload(file, "blind_cv")}
              isUploading={uploadingBlindCV}
              viewLabel={t("common:fileUpload.viewBlindCv")}
              uploadLabel={t("common:fileUpload.uploadBlindCv")}
            />
            <FileUploadWidget
              label={t("common:fileUpload.cvLabel")}
              fileUrl={candidate?.resume_url}
              onUpload={(file) => handleFileUpload(file, "cv")}
              isUploading={uploadingCV}
              viewLabel={t("common:fileUpload.viewCv")}
              uploadLabel={t("common:fileUpload.uploadCv")}
            />
            <div className="tech-check-field">
              <label htmlFor="tech-check-blind-cv-review">
                {t("techCheck.blindCvReview")}
              </label>
              <textarea
                id="tech-check-blind-cv-review"
                value={formData.blind_cv_review}
                onChange={(e) =>
                  handleInputChange("blind_cv_review", e.target.value)
                }
                rows={4}
              />
            </div>
          </div>

          <div className="tech-check-section">
            <h3>{t("techCheck.survey")}</h3>
            <div className="tech-check-field">
              <label>
                <input
                  type="checkbox"
                  checked={formData.survey_complete}
                  onChange={(e) =>
                    handleInputChange("survey_complete", e.target.checked)
                  }
                />
                {t("techCheck.surveyComplete")}
              </label>
            </div>
            <div className="tech-check-field">
              <label htmlFor="tech-check-survey-link">
                {t("techCheck.surveyLink")}
              </label>
              <input
                id="tech-check-survey-link"
                type="text"
                value={formData.survey_link}
                onChange={(e) =>
                  handleInputChange("survey_link", e.target.value)
                }
                placeholder={t("techCheck.surveyLinkPlaceholder")}
              />
            </div>
          </div>

          <div className="tech-check-section">
            <h3>{t("techCheck.guide")}</h3>
            <div className="info-field">
              <span className="info-label">
                {t("techCheck.guideForTechCheck")}
              </span>
              <div className="info-value">
                <a
                  href="https://dev.azure.com/incredevo/Time-and-Material/_wiki/wikis/Time-and-Material.wiki/1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("techCheck.viewGuide")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechCheckForm;
